import * as THREE from "three";
import { fingerprintMaps } from "./detail";
import { NEUTRAL_BUILD, type ArtStyle, type Surface } from "./types";

// Clay (#76): soft plasticine on a toy diorama. Every surface is matte,
// slightly waxy modelling clay in a pastel version of its color, pressed with
// fingerprints (a normal map of overlapping thumb whorls). Light is soft and
// warm with blurry-edged shadows, and on High the post chain adds ambient
// occlusion (clay pieces sitting in each other's shade) and a tilt-shift blur
// toward the top and bottom of the frame, so the park reads as a miniature.
// No ink: claymation has none. Low draws none of the post (cost medium).

/** How far each color moves toward pastel: lighter, and some saturation taken out. */
const PASTEL = { light: 0.3, toward: 0.8, saturation: 0.72 };
/** How strongly the fingerprints bend the light. */
const PRINTS = 0.45;

export const clay: ArtStyle = {
  id: "clay",
  name: "Clay",
  pitch: "Plasticine toy diorama: pastel clay pressed with fingerprints, soft shade, tilt-shift miniature blur.",
  cost: "medium",

  material(color, params, surface: Surface) {
    const c = new THREE.Color(color);
    const hsl = { h: 0, s: 0, l: 0 };
    c.getHSL(hsl);
    c.setHSL(hsl.h, hsl.s * PASTEL.saturation, hsl.l + (PASTEL.toward - hsl.l) * PASTEL.light);
    // Glass stays glass (windows still read); everything else is clay.
    if (surface === "glass") return new THREE.MeshStandardMaterial({ ...params, color: c, roughness: 0.2, metalness: 0 });
    const maps = fingerprintMaps(3);
    return new THREE.MeshStandardMaterial({
      ...params,
      color: c,
      roughness: 0.78,
      metalness: 0,
      ...(maps ? { normalMap: maps.normal, normalScale: new THREE.Vector2(PRINTS, PRINTS), roughnessMap: maps.roughness } : {}),
    });
  },

  outlines: { scale: 0, ink: 0x000000, edges: 0 },

  lighting: {
    sunColor: 0xfff1de,
    sun: 0.85,
    // A bright, soft fill like a studio light box over the diorama.
    hemi: 1.0,
    environment: "studio",
    envIntensity: { outdoor: 0.25, indoor: 0.25 },
  },

  sky: { clouds: true },
  fog: { near: 1, far: 1 },

  // Neutral keeps the pastels from washing out or going muddy.
  toneMapping: THREE.NeutralToneMapping,
  exposure: 1,

  post: {
    ao: { radius: 1, intensity: 1 },
    bloom: null,
    grade: { contrast: 1.04, saturation: 1.05, warmth: 0.03, vignette: 0.18 },
    tiltShift: { focus: 0.45, band: 0.3, blur: 10 },
    mood: null,
    dof: null,
  },

  dust: null,

  resolution: null,

  // The chunky toy rider already looks sculpted.
  rider: NEUTRAL_BUILD,

  // Soft, blurry-edged shadows.
  shadows: { mapSize: 2048, radius: 6, bias: -0.0005, normalBias: 0.04 },
};
